import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import NavDrawer from './NavDrawer';
import { imageUrl } from './CardImage';

// Conversation avatar: shows the listed card's front photo so threads are
// distinguishable at a glance. Photos live under the seller's storage path
// (CK convention: participant_1 = buyer, participant_2 = seller), so the owner
// id is the conversation's participant_2. Falls back to the counterparty's
// initial if the card has no front photo (or it fails to load).
function ConvAvatar({ ownerId, cardKey, fallbackChar }) {
  const [failed, setFailed] = useState(false);
  const src = cardKey && ownerId ? imageUrl(ownerId, cardKey, 'front') : null;
  if (src && !failed) {
    return <img src={src} alt="card" style={S.avatarImg} onError={() => setFailed(true)} />;
  }
  return <div style={S.avatar}>{fallbackChar}</div>;
}

// Last-message preview: real text, or a placeholder for attachment-only messages.
function previewText(m) {
  if (m.body && m.body.trim()) return m.body;
  if (m.attachment_path) {
    return (m.attachment_type && m.attachment_type.startsWith('image/')) ? '📷 Photo' : '📎 Attachment';
  }
  return m.body || '';
}

export default function Messages({ user }) {
  const [conversations, setConversations] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);

  const nav = path => { window.history.pushState({}, '', path); window.dispatchEvent(new PopStateEvent('popstate')); };

  useEffect(() => {
    loadConversations();
    const sub = supabase.channel('conversations-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, loadConversations)
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [user.id]);

  const loadConversations = async () => {
    const { data: convs } = await supabase.from('conversations').select('*')
      .or(`participant_1.eq.${user.id},participant_2.eq.${user.id}`)
      .order('updated_at', { ascending: false });
    if (!convs?.length) { setLoading(false); return; }

    const { data: unreadRows } = await supabase.rpc('my_unread_counts');
    const unreadMap = {};
    (unreadRows || []).forEach(r => { unreadMap[r.conversation_id] = r.unread; });
    const enriched = await Promise.all(convs.map(async c => {
      const { data: msgs } = await supabase.from('messages').select('*')
        .eq('conversation_id', c.id).order('created_at', { ascending: false }).limit(1);
      return { ...c, lastMessage: msgs?.[0] || null, unreadCount: unreadMap[c.id] || 0 };
    }));

    const otherIds = [...new Set(enriched.map(c => c.participant_1 === user.id ? c.participant_2 : c.participant_1))];
    const { data: profileData } = await supabase.from('public_profiles').select('id,username').in('id', otherIds);
    const profileMap = {};
    profileData?.forEach(p => profileMap[p.id] = p);
    setProfiles(profileMap);
    setConversations(enriched);
    setLoading(false);
  };

  const formatTime = ts => {
    if (!ts) return '';
    const d = new Date(ts), now = new Date(), diff = now - d;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return Math.floor(diff/60000) + 'm ago';
    if (diff < 86400000) return Math.floor(diff/3600000) + 'h ago';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div style={S.page}>
      <NavDrawer user={user} />
      <header style={S.header}>
        <div style={S.headerInner}>
          <div>
            <div style={S.logoLine}>COLLECTIONKEEPER · MESSAGES</div>
            <h1 style={S.title}>MY <span style={{ color: "var(--brand-gold)" }}>MESSAGES</span></h1>
          </div>
        </div>
        <div style={S.headerLine} />
      </header>
      <main style={S.main}>
        {loading && <div style={S.empty}>Loading...</div>}
        {!loading && conversations.length === 0 && (
          <div style={S.empty}>
            <div style={{ fontSize: "32px", marginBottom: "12px" }}>✉</div>
            <div style={S.emptyTitle}>No messages yet</div>
            <div style={S.emptySub}>Start a conversation from a Marketplace listing</div>
          </div>
        )}
        {conversations.map(c => {
          const otherId = c.participant_1 === user.id ? c.participant_2 : c.participant_1;
          const other = profiles[otherId];
          const snap = c.card_snapshot;
          return (
            <div key={c.id} style={S.row} onClick={() => nav("/messages/" + c.id)}
              onMouseEnter={e => e.currentTarget.style.borderColor = "var(--cyan)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
              <ConvAvatar ownerId={c.participant_2} cardKey={c.card_key}
                fallbackChar={(other?.username || "?").charAt(0).toUpperCase()} />
              <div style={S.rowMid}>
                <div style={S.rowTop}>
                  <span style={S.username}>@{other?.username || "..."}</span>
                  {c.unreadCount > 0 && <span style={S.badge}>{c.unreadCount}</span>}
                </div>
                {snap && <div style={S.cardSnap}>{snap.setName} · Card #{snap.cardN}{snap.parallelName ? " · " + snap.parallelName : ""}</div>}
                <div style={S.lastMsg}>{c.lastMessage ? (c.lastMessage.sender_id === user.id ? "You: " : "") + previewText(c.lastMessage) : "No messages yet"}</div>
              </div>
              <div style={S.rowRight}>
                <div style={S.time}>{formatTime(c.updated_at)}</div>
                <div style={{ color: "var(--border-2)", fontSize: "18px" }}>›</div>
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "var(--bg)", color: "var(--text)", fontFamily: "'Rajdhani',sans-serif" },
  header: { background: "rgba(var(--bg-rgb),0.97)", padding: "20px 24px 0", borderBottom: "1px solid var(--border)", position: "relative", zIndex: 10 },
  headerInner: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", maxWidth: "700px", margin: "0 auto", paddingBottom: "16px" },
  logoLine: { fontFamily: "'Orbitron',monospace", fontSize: "8px", letterSpacing: "4px", color: "var(--brand-gold)", opacity: 0.85, marginBottom: "6px" },
  title: { fontFamily: "'Orbitron',monospace", fontSize: "clamp(18px,3vw,28px)", fontWeight: 900, color: "#fff", margin: 0 },
  headerLine: { height: "1px", background: "linear-gradient(90deg,transparent,var(--cyan),var(--brand-gold),var(--cyan),transparent)" },
  main: { padding: "24px", maxWidth: "700px", margin: "0 auto" },
  empty: { textAlign: "center", padding: "80px 20px", fontFamily: "'Share Tech Mono',monospace", fontSize: "12px", color: "var(--border-2)" },
  emptyTitle: { fontFamily: "'Orbitron',monospace", fontSize: "14px", color: "var(--muted-2)", marginBottom: "8px" },
  emptySub: { fontFamily: "'Share Tech Mono',monospace", fontSize: "11px", color: "var(--border-2)" },
  row: { display: "flex", alignItems: "center", gap: "14px", background: "rgba(var(--pcard-rgb),0.85)", border: "1px solid var(--border)", borderRadius: "3px", padding: "16px", marginBottom: "10px", cursor: "pointer", transition: "border-color 0.15s" },
  avatar: { width: "40px", height: "40px", borderRadius: "50%", background: "rgba(var(--cyan-rgb),0.1)", border: "1px solid rgba(var(--cyan-rgb),0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron',monospace", fontSize: "16px", fontWeight: 700, color: "var(--cyan)", flexShrink: 0 },
  avatarImg: { width: "40px", height: "40px", borderRadius: "6px", objectFit: "cover", border: "1px solid rgba(var(--brand-gold-rgb),0.45)", background: "var(--bg-3)", flexShrink: 0 },
  rowMid: { flex: 1, minWidth: 0 },
  rowRight: { flexShrink: 0, textAlign: "right" },
  rowTop: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" },
  username: { fontFamily: "'Orbitron',monospace", fontSize: "12px", fontWeight: 700, color: "#fff" },
  badge: { background: "var(--cyan)", color: "var(--bg)", fontFamily: "'Orbitron',monospace", fontSize: "9px", fontWeight: 700, borderRadius: "10px", padding: "2px 6px" },
  cardSnap: { fontFamily: "'Share Tech Mono',monospace", fontSize: "10px", color: "var(--brand-gold)", marginBottom: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  lastMsg: { fontFamily: "'Share Tech Mono',monospace", fontSize: "11px", color: "var(--muted-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  time: { fontFamily: "'Share Tech Mono',monospace", fontSize: "10px", color: "var(--border-2)", marginBottom: "4px" },
};
